import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { renderHourTick } from "../../../../components/recharts/components";
import { useDispatch, useSelector } from "react-redux";
import FilterMenu from "../../../../components/FilterMenu";
import { setFilterWidth } from "../../../../store/state/FilterWidth";
import { R_CHART_TYPES } from "../../const";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";

const X_AXIS = [
  {
    label: "Time Slot",
    key: "time_slot__slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Class",
    key: "class",
    values: ["class", "date"],
    isTime: false,
  },
];

const VARIABLE_OPTIONS = [
  {
    suffix: "total_capacity",
    displayName: "Declared Capacity",
    stackSuffix: "total_capacity",
    chartType: ChartType.Bar,
    stroke: "rgba(255, 171, 121, 1)",
    legendColor: "rgba(255, 171, 121, 1)",
    unit: "MW",
    yAxisId: "left",
  },
  {
    suffix: "must_run_value",
    displayName: "Must Run",
    stackSuffix: "must_run_value",
    chartType: ChartType.Bar,
    stroke: "rgba(132, 187, 199, 1)",
    legendColor: "rgba(132, 187, 199, 1)",
    unit: "MW",
    yAxisId: "left",
  },
  {
    suffix: "class",
    displayName: "Class",
    stackSuffix: "class",
    chartType: ChartType.Bar,
    stroke: "rgba(162, 132, 199, 1)",
    legendColor: "rgba(162, 132, 199, 1)",
    unit: "",
    yAxisId: "left",
  },
  {
    suffix: "vc",
    displayName: "VC",
    stackSuffix: "vc",
    chartType: ChartType.Line,
    stroke: "#04046b",
    legendColor: "#04046b",
    unit: "Rs/kWh",
    yAxisId: "right",
  },
];

export default function ClassesChart({
  selectedBlock,
  data,
}: {
  selectedBlock: number[];
  data: any[];
}) {
  const [open, setOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedVariables, setSelectedVariables] = useState<any[]>([
    VARIABLE_OPTIONS[0],
  ]);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[0]);
  const preSelectedDate = useSelector(
    (state: any) => state.modGridcoDate.selectedDate
  );

  const isClassXAxis = selectedInterval.key === "class";

  function _handleVariableChange(index: number) {
    const item = VARIABLE_OPTIONS[index];
    // "Class" variable cannot be selected when X Axis is "Class"
    if (item.suffix === "class" && isClassXAxis) return;
    
    if (selectedVariables.includes(item)) {
      if (selectedVariables.length === 1) return;
      setSelectedVariables(selectedVariables.filter((v) => v !== item));
    } else {
      setSelectedVariables(
        [...selectedVariables, item].sort(
          (a, b) => VARIABLE_OPTIONS.indexOf(a) - VARIABLE_OPTIONS.indexOf(b)
        )
      );
    }
  }

  useEffect(() => {
    // When X axis changes to Class, drop Class from selected variables
    if (isClassXAxis) {
      setSelectedVariables((prev) => {
        const filtered = prev.filter((v) => v.suffix !== "class");
        return filtered.length > 0 ? filtered : [VARIABLE_OPTIONS[0]];
      });
    }
  }, [isClassXAxis]);

  useEffect(() => {
    function toSortableNumber(value: unknown) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    }

    function resolveMustRunValue(item: any) {
      const mustRunMw = Number(item.must_run_mw);
      if (Number.isFinite(mustRunMw)) return mustRunMw;
      const totalCapacity = Number(item.total_capacity);
      const mustRun = Number(item.must_run);
      if (Number.isFinite(mustRun) && Number.isFinite(totalCapacity) && mustRun >= 0 && mustRun <= 100) {
        return (totalCapacity * mustRun) / 100;
      }
      return Number.isFinite(mustRun) ? mustRun : 0;
    }

    let filteredData = (data || []).map((item) => ({
      ...item,
      class: item.generator_class,
      must_run_value: resolveMustRunValue(item),
      date: preSelectedDate || "2026-04-14",
    }));

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.includes(Number(item.time_slot__slot))
      );
    }

    if (selectedInterval.key === "time_slot__slot") {
      filteredData = [...filteredData].sort(
        (a, b) =>
          toSortableNumber(a.time_slot__slot) -
          toSortableNumber(b.time_slot__slot)
      );
    } else if (selectedInterval.key === "class") {
      filteredData = [...filteredData].sort(
        (a, b) => toSortableNumber(a.class) - toSortableNumber(b.class)
      );
    }

    function getKeys() {
      if (selectedInterval.key === "time_slot__slot") {
        return ["date", "time_slot__slot"];
      } else if (selectedInterval.key === "class") {
        return ["date", "class"];
      }
      return [selectedInterval.values[0]];
    }

    const allSuffixes = VARIABLE_OPTIONS.map((v) => v.suffix);
    
    // For Class X-axis: average everything (multiple slots per class).
    // For Time Slot X-axis: sum volume metrics, average rate (vc) and class number per slot.
    // "class" (generator_class number) must always be averaged — summing class numbers is meaningless.
    const shouldAverageAll = selectedInterval.key === "class";

    const averageKeys = shouldAverageAll
      ? allSuffixes
      : allSuffixes.filter((s) => s === "vc" || s === "class");

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "time_slot__slot", "class"],
      groupBy: [""],
      volumeKey: "",
      aggregationKeys: allSuffixes,
      average: true,
      averageKeys,
      averageDuplicationKeys: ["date", "time_slot__slot", "class"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    let sortedChartData = cData.data;

    if (selectedInterval.key === "time_slot__slot") {
      sortedChartData = [...cData.data].sort(
        (a, b) =>
          toSortableNumber(a.time_slot__slot) -
          toSortableNumber(b.time_slot__slot)
      );
    } else if (selectedInterval.key === "class") {
      sortedChartData = [...cData.data].sort(
        (a, b) => toSortableNumber(a.class) - toSortableNumber(b.class)
      );
    }

    setChartData(sortedChartData);
    setChartKeys(cData.keys);
  }, [
    data,
    preSelectedDate,
    selectedBlock,
    selectedInterval.key,
    selectedInterval.values,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string
  ) => {
    const forcedLine = chartType === ChartType.Line;
    const resolvedType = forcedLine ? ChartType.Line : selectedChartType.value;

    const shouldStack =
      !forcedLine &&
      selectedChartType.stackId !== undefined &&
      selectedChartType.value === ChartType.Bar;

    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      type: resolvedType,
      unit: unit,
      stroke: stroke,
      legendColor: legendColor,
      yAxisId: yAxisId,
      stackId: shouldStack ? key : undefined,
    };
  };

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      return "time_slot__slot";
    }
    return selectedInterval.key;
  }, [selectedInterval.isTime, selectedInterval.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    return "date";
  }

  function getLegends() {
    return selectedVariables.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        stroke,
        legendColor,
        yAxisId,
      }) =>
        chartKeys.map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            chartType,
            unit,
            stroke,
            legendColor,
            yAxisId
          )
        )
    );
  }

  const handleWidthChange = useMemo(
    () => (width: number) => {
      if (open) {
        const roundedWidth = Math.ceil(width + 30);
        const pixelValue = `${roundedWidth}px`;
        const pageWidth = `calc(100% - ${pixelValue} - 35px)`;
        dispatch(setFilterWidth(pageWidth));
      }
    },
    [dispatch, open]
  );

  const debouncedHandleWidthChange = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<typeof handleWidthChange>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        handleWidthChange(...args);
        timeoutId = null;
      }, 100);
    };
  }, [handleWidthChange]);

  useEffect(() => {
    if (!containerRef.current) return;
    const currentContainer = containerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        debouncedHandleWidthChange(entry.contentRect.width);
      }
    });
    resizeObserver.observe(currentContainer);
    return () => {
      resizeObserver.unobserve(currentContainer);
    };
  }, [debouncedHandleWidthChange]);

  useEffect(() => {
    if (!open) {
      const pageWidth = `calc(100% - 70px)`;
      dispatch(setFilterWidth(pageWidth));
    }
  }, [open, dispatch]);

  return (
    <div className="flex max-h-[calc(100vh-300px)]">
      <div className="h-100 border border-[#718EBF1C] rounded-lg shadow-[rgba(149,157,165,0.2)_0px_8px_24px]">
        <div className="hide-btn-outer">
          <div
            ref={containerRef}
            className="transition-all duration-700 ease-in-out bg-white m-[0.8rem] me-1 rounded-2xl"
            style={{
              width: open ? "auto" : "0px",
              height: "calc(100vh - 328px)",
              transition: "all 0.3s ease-in-out, opacity 0.3s ease-in-out",
              padding: open ? "0px" : "12px",
              paddingRight: open ? "1rem" : "12px",
            }}
          >
            <div
              className="rounded-2xl"
              style={{
                height: "calc(100vh - 328px)",
                overflowY: open ? "auto" : "hidden",
              }}
            >
              <div
                style={{ opacity: open ? 1 : 0 }}
                className="transition-all duration-700 ease-in-out"
              >
                <div className="side-filters-container text-nowrap">
                  <FilterMenu
                    title="X Axis"
                    items={X_AXIS.map((item) => item.label)}
                    selectedItems={[selectedInterval.label]}
                    onSelect={(item, index) => {
                      setSelectedInterval(X_AXIS[index]);
                    }}
                  />
                  <FilterMenu
                    title="Variable"
                    items={VARIABLE_OPTIONS.map((item) => item.displayName)}
                    selectedItems={selectedVariables.map(
                      (item) => item.displayName
                    )}
                    disabledItems={isClassXAxis ? ["Class"] : []}
                    disabledTooltip="Class option is not allowed when X-Axis is Class"
                    onSelect={(item, index) => {
                      _handleVariableChange(index);
                    }}
                  />
                  <FilterMenu
                    title="Chart Type"
                    items={R_CHART_TYPES.map((item) => item.label)}
                    selectedItems={[selectedChartType.label]}
                    onSelect={(item, index) => {
                      setSelectedChartType(R_CHART_TYPES[index]);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 h-100 ml-4 shadow-[rgba(149,157,165,0.2)_0px_8px_24px]">
        <div className="dash-card col-span-1 md:col-span-6 border border-[#718EBF1C] !bg-[] !shadow-none !h-full w-full">
          <ReMixChart
            chartHeight="calc(100% - 50px)"
            data={chartData}
            showBrush={true}
            brushStart={BrushStart.Start}
            isTimeSlot={selectedInterval.isTime}
            onlyTitle={true}
            legends={getLegends()}
            yAxisLabel={selectedVariables.some((v) => v.yAxisId === "left") ? "MW" : undefined}
            secondYAxisLabel={selectedVariables.some((v) => v.yAxisId === "right") ? "Rs/kWh" : undefined}
            nameGap={40}
            xDataKey={xDataKey}
            top={10}
            bottom={30}
            xTick={
              selectedInterval.isTime && selectedBlock.length === 0
                ? (value: any, index: any) => renderHourTick(value, 10)
                : undefined
            }
            secondXDataKey={secondDataKey()}
            showToolbox={false}
          />
        </div>
      </div>
    </div>
  );
}
